// PhioengageDischargedList.tsx
import React from 'react';
import { Box, Chip, Container, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { firestoreInstance } from 'config/ApiService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import MaterialTable, { MTableToolbar } from 'material-table';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { useNavigate } from 'react-router-dom';
import { MultiFilter } from 'views/triage/components/Filter';
import { AGREEMENT_TYPE, ClinicalServicesClient } from 'components/ClinicalServicesDropdown';
import { dataQuery } from 'utils/remote-fetch';
import { logGoogleAnalyticsEvent, logScreenView } from 'utils/gaLog';
import { setUserActivity } from 'services/trackUserActivity';

export const USE_CASE_LIST = [
    { id: 'all', name: 'All' },
    { id: 'cash_plan', name: 'Cash Plan' },
    { id: 'primary_care', name: 'Primary Care' },
    { id: 'secondary_care', name: 'Secondary Care' },
    { id: 'waiting_list', name: 'Waiting List' }
];

export const USE_CASE_COLORS: Record<string, string> = {
    cash_plan: '#4caf50',
    primary_care: '#2196f3',
    secondary_care: '#ff9800',
    waiting_list: '#f44336'
};

export const dischargeReasons = [
    { id: 'failed_to_onboard', name: 'Failed to Onboard' },
    { id: 'episode_closed', name: 'Episode Closed' },
    { id: 'episode_complete', name: 'Episode Complete' },
    { id: 'onward_referral', name: 'Onward Referral' },
    { id: 'auto_discharge', name: 'Auto Discharge' },
    { id: 'other', name: 'Other' }
];

const PhioengageDischargedList = () => {
    const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
    const client = useSelector((state: RootState) => state.AuthReducer.organisation);
    const permissions = useSelector((state: RootState) => state.AuthReducer.permissions);
    const [filterList, setFilterList] = React.useState<ClinicalServicesClient[]>([]);

    // filters
    const [clientFilter, setClientFilter] = React.useState<string[]>(['all']);
    const [usecaseFilter, setUsecaseFilter] = React.useState<string[]>(['all']);

    const mtTableRef = React.useRef<any>(null);

    const navigate = useNavigate();

    const isInternalClinicalServices = permissions.includes('internal_clinical_services');

    const applyCustomFilterToResults = (data: any[]) => {
        if (isInternalClinicalServices && clientFilter.includes('all')) {
            const clientIds = filterList.map((c) => c.id);
            // filter the data based on the clientIds
            return data.filter((item) => clientIds.includes(item.customer_id));
        }
        return data;
    };

    const handleClientFilterChange = (val: string[]) => {
        setClientFilter(val);
        // hack to force component to refresh
        forceUpdate();
    };

    const handleUsecaseFilterChange = (val: string[]) => {
        setUsecaseFilter(val);
        // hack to force component to refresh
        forceUpdate();
    };

    const buildQueryConstraints = React.useCallback(() => {
        const qConstraints: any[] = [];
        if (isInternalClinicalServices && usecaseFilter.length && !usecaseFilter.includes('all')) {
            qConstraints.push({ name: 'customer_preset', op: 'in', value: usecaseFilter });
        }
        if (
            isInternalClinicalServices &&
            clientFilter &&
            clientFilter.length > 0 &&
            !clientFilter.includes('all')
        ) {
            qConstraints.push({ name: 'customer_id', op: 'in', value: clientFilter });
        }
        if (!isInternalClinicalServices) {
            qConstraints.push({ name: 'customer_id', op: '==', value: client.id });
        }
        qConstraints.push({ name: 'case_status', op: '==', value: 'discharged' });
        return qConstraints;
    }, [isInternalClinicalServices, usecaseFilter, clientFilter, client.id]);

    React.useEffect(() => {
        (async () => {
            const ref = collection(firestoreInstance(), 'clients');
            const q = query(
                ref,
                where('agreement_type', 'in', [
                    AGREEMENT_TYPE.ENGAGE,
                    AGREEMENT_TYPE.ACCESS_AND_ENGAGE,
                    AGREEMENT_TYPE.ACCESS_ENGAGE_AND_COLLECT
                ]),
                where('clinical_services_enabled', '==', true)
            );
            const snapshots = await getDocs(q);
            const clients: ClinicalServicesClient[] = [];
            snapshots.forEach((doc) => {
                const data = doc.data();
                clients.push({
                    id: doc.id,
                    name: data.name,
                    agreement_type: data.agreement_type
                });
            });
            clients.sort((a, b) => a.name.localeCompare(b.name));
            clients.unshift({
                id: 'all',
                name: 'All'
            } as ClinicalServicesClient);
            setFilterList(clients);
        })();
    }, []);

    React.useEffect(() => {
        if (mtTableRef.current) {
            // eslint-disable-next-line no-unused-expressions
            mtTableRef.current.onQueryChange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mtTableRef.current, clientFilter, client, usecaseFilter]);

    React.useEffect(() => {
        logScreenView('discharge_page_view');
    }, []);

    return (
        <Box minWidth="650px">
            <MaterialTable
                title={`Discharged Patients List - ${
                    isInternalClinicalServices
                        ? 'All Internal Clinical Service Customers'
                        : client.name
                }`}
                tableRef={mtTableRef}
                data={(query) =>
                    dataQuery(query, {
                        remoteCollectionName: 'phioengage_user_profiles',
                        fields: buildQueryConstraints(),
                        customSort: applyCustomFilterToResults
                    })
                }
                columns={[
                    {
                        title: 'UID',
                        field: 'id',
                        sorting: false,
                        render: (rowData: any) => (
                            <Typography variant="body2">{rowData.id}</Typography>
                        )
                    },
                    {
                        title: 'Patient',
                        field: 'first_name',
                        sorting: false,
                        render: (rowData: any) => (
                            <Typography variant="body2" color="textSecondary">
                                {rowData.first_name + ' ' + rowData?.last_name}
                            </Typography>
                        )
                    },
                    {
                        title: 'Email',
                        field: 'email',
                        sorting: false
                    },
                    {
                        title: 'Customer',
                        field: 'customer_name',
                        sorting: false,
                        hidden: !permissions.includes('internal_clinical_services'),
                        render: (rowData: any) => {
                            if (!rowData.customer_name) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.customer_name}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Programme',
                        field: 'programme_name',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.programme_name) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.programme_name}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Use Case',
                        field: 'customer_use_case',
                        sorting: false,
                        hidden: !permissions.includes('internal_clinical_services'),
                        render: (rowData: any) => {
                            if (!rowData.customer_preset) {
                                return '-';
                            }
                            return (
                                <Chip
                                    label={rowData.customer_preset.split('_').join(' ')}
                                    size="small"
                                    sx={{
                                        textTransform: 'capitalize',
                                        border: `2px solid ${
                                            USE_CASE_COLORS[`${rowData.customer_preset}`]
                                        }`
                                    }}
                                    variant="outlined"
                                />
                            );
                        }
                    },
                    {
                        title: 'Discharge Date',
                        field: 'discharge_date',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.discharge_date) {
                                return '';
                            }
                            return (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ textTransform: 'capitalize' }}
                                >
                                    {rowData.discharge_date.toDate().toLocaleDateString('en-GB', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit'
                                    })}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Discharge Reason',
                        field: 'discharge_reason',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.discharge_reason) {
                                return '-';
                            }
                            return (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ textTransform: 'capitalize' }}
                                >
                                    {
                                        dischargeReasons.find(
                                            (reason) => reason.id === rowData.discharge_reason
                                        )?.name
                                    }
                                </Typography>
                            );
                        },
                        emptyValue: '-'
                    },
                    {
                        title: 'Triage Clinician',
                        field: 'triage_clinician',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.triage_clinician) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.triage_clinician}
                                </Typography>
                            );
                        }
                    }
                ]}
                options={{
                    pageSize: 20,
                    pageSizeOptions: [20, 40, 60],
                    searchFieldVariant: 'outlined',
                    filtering: false,
                    showFirstLastPageButtons: false,
                    paging: true
                }}
                components={{
                    Toolbar: (props) => (
                        <Box m={1}>
                            <MTableToolbar {...props} />
                            <Container maxWidth="xl">
                                <Grid container spacing={2} marginTop={1}>
                                    {permissions.includes('internal_clinical_services') && (
                                        <Grid size={{ xs: 6, lg: 3 }}>
                                            <MultiFilter
                                                value={clientFilter ? clientFilter : []}
                                                options={filterList.map((c) => ({
                                                    id: c.id,
                                                    name: c.name
                                                }))}
                                                onChange={handleClientFilterChange}
                                                label="Customer"
                                            />
                                        </Grid>
                                    )}
                                    {permissions.includes('internal_clinical_services') && (
                                        <Grid size={{ xs: 6, lg: 3 }}>
                                            <MultiFilter
                                                value={usecaseFilter}
                                                options={USE_CASE_LIST}
                                                onChange={handleUsecaseFilterChange}
                                                label="Use Case"
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Container>
                        </Box>
                    )
                }}
                actions={[
                    (rowData: any) => ({
                        icon: 'visibility',
                        tooltip: 'View Patient',
                        onClick: () => {
                            logGoogleAnalyticsEvent('discharge_list_view_patient_click');
                            navigate(`/dashboard/phioengage-profile/${rowData.id}`);
                            setUserActivity({
                                patientId: rowData.id,
                                eventName: 'details_screen_visited',
                                eventDetails: { visited_from: 'discharged_patients_list' }
                            });
                        }
                    })
                ]}
                localization={{
                    toolbar: {
                        searchPlaceholder: 'UID, First/Last Name, Email, Customer, Programme'
                    },
                    pagination: {
                        labelRowsPerPage: '',
                        labelDisplayedRows: ''
                    }
                }}
            />
        </Box>
    );
};

export default PhioengageDischargedList;
